"use client";

import { Suspense, useEffect, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/features/auth/useAuth";
import { AppShell } from "@/components/ui/AppShell";
import { BreadcrumbProvider } from "@/components/ui/BreadcrumbContext";
import { PackDeniedToast } from "@/components/ui/PackDeniedToast";
import { FilterProvider } from "@/features/filters/FilterContext";
import { HelpPanelProvider } from "@/features/help/HelpPanelContext";
import { DashboardContentsProvider } from "@/features/london-dashboard/DashboardContentsContext";
import { InspectProvider } from "@/features/inspect/InspectContext";
import { ExplainPanelProvider } from "@/features/report/explain/ExplainPanelContext";

interface AuthLayoutProps {
  children: ReactNode;
}

export default function AuthLayout({
  children,
}: AuthLayoutProps): React.JSX.Element | null {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      router.replace("/login");
    }
  }, [isAuthenticated, isLoading, router]);

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <HelpPanelProvider>
      <InspectProvider>
        {/*
          Out-of-map edit (mission drilldown-substrate, WP05 cycle-2, Issue 1 — declared,
          minimal): `ExplainPanelProvider` was documented in its own module docstring as
          "mounted once ... (auth)/layout.tsx, alongside those two [HelpPanelProvider/
          InspectProvider]" but no commit had ever actually added it here — every production
          `useExplainPanel()` consumer (AppShell's splitPanel wiring, every ExplainCellButton)
          got the DEFAULT no-op context, so clicking a figure did nothing in the running app
          (cycle-1 review, Issue 1: live-verified zero requests, aria-expanded stuck false).
          The WP05/T017-T020 test suite never caught it because every test hand-mounts this
          provider itself. Mounted here, a peer of Help/Inspect exactly as the docstring always
          claimed; see `__tests__/explain/productionMount.test.tsx` for the regression guard —
          it renders this real layout file with NO hand-mounted provider.
        */}
        <ExplainPanelProvider>
          <FilterProvider>
            <BreadcrumbProvider>
              {/*
                Above AppShell, exactly like BreadcrumbProvider and for the same reason: the
                dashboard page publishes the sections it rendered, and the SIDEBAR — which is
                AppShell's, not the page's — is what renders them. A provider mounted inside
                AppShell could not be read by AppShell itself.
              */}
              <DashboardContentsProvider>
                <AppShell>
                  {/*
                PackDeniedToast lives at the layout level so the toast
                fires regardless of which route RouteGuard redirects to.
                Today the redirect target is /dashboard/gla-summary
                (RouteGuard.REDIRECT_TARGET); mounting here means a future
                destination change won't silently drop the toast. The
                toast is a no-op when `?packDenied` is absent, so the
                universal mount has no cost on normal navigation. Suspense
                boundary is required by Next 16's `useSearchParams`.
              */}
                  <Suspense fallback={null}>
                    <PackDeniedToast />
                  </Suspense>
                  {children}
                </AppShell>
              </DashboardContentsProvider>
            </BreadcrumbProvider>
          </FilterProvider>
        </ExplainPanelProvider>
      </InspectProvider>
    </HelpPanelProvider>
  );
}
